import React, { useEffect, useState } from 'react';
import BOLayout from '@/pages/back-office/BOLayout';
import axios from 'axios';
import { toast } from 'react-toastify';
import Pagination from '@/components/back-office/Pagination';
import FilterComponent from '@/components/back-office/FilterComponent';
import MagnifyingGlass from '@/assets/media/SVGComponents/MagnifyingGlass';
import { useNavigate } from 'react-router-dom';
import useWebSocket from '@/hooks/useWebSocket';
import events from '@/constants/websocketEvents';

const BODashboard = () => {
	const [dailyOrders, setDailyOrders] = useState(0);
	const [shabbatOrders, setShabbatOrders] = useState(0);
	const [pendingLabels, setPendingLabels] = useState(0);

	const [orders, setOrders] = useState([]);
	const [total, setTotal] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const ordersPerPage = 10;
	const [filters, setFilters] = useState({});

	const [restaurantOptions, setRestaurantOptions] = useState([]);
	const [statusOptions, setStatusOptions] = useState([]);

	const navigate = useNavigate();

	const statuses = {
		pending: {
			label: 'En attente de validation',
			bgColor: 'bg-[#FF8A00]',
		},
		validated: {
			label: 'Virement',
			bgColor: 'bg-[#02C65C]',
		},
		canceled: {
			label: 'Annulée',
			bgColor: 'bg-[#FF0000]',
		},
		pending_payment: {
			label: 'En attente de paiement',
			bgColor: 'bg-[#FF8A00]',
		},
		paid: {
			label: 'Payée',
			bgColor: 'bg-[#02C65C]',
		}
	};

	const fetchDashboardData = async () => {
		try {
			const dailyOrdersResponse = await axios.get(
				`${import.meta.env.VITE_API_URL}/api/back-office/orders/daily-count`
			);
			const shabbatOrdersResponse = await axios.get(
				`${import.meta.env.VITE_API_URL}/api/back-office/orders/shabbat-count`
			);
			const pendingLabelsResponse = await axios.get(
				`${import.meta.env.VITE_API_URL}/api/back-office/orders/pending-labels-count`
			);

			setDailyOrders(dailyOrdersResponse.data.count);
			setShabbatOrders(shabbatOrdersResponse.data.count);
			setPendingLabels(pendingLabelsResponse.data.count);
		} catch (error) {
			console.error('Erreur lors du chargement des données du tableau de bord :', error);
			toast.error('Erreur lors du chargement des données du tableau de bord', {
				position: 'top-center',
				autoClose: 3000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});
		}
	};

	const fetchFilterOptions = async () => {
		try {
			const restaurantsResponse = await axios.get(`${import.meta.env.VITE_API_URL}/api/back-office/restaurants`);
			const restaurants = restaurantsResponse.data.map((restaurant) => ({
				label: restaurant.name,
				value: restaurant.id,
			}));
			setRestaurantOptions([{ label: 'Tous', value: '' }, ...restaurants]);

			const statusesResponse = await axios.get(`${import.meta.env.VITE_API_URL}/api/back-office/orders/statuses`);
			const statuses = statusesResponse.data.map((status) => ({
				label: status.label,
				value: status.value,
			}));
			setStatusOptions([{ label: 'Tous', value: '' }, ...statuses]);
		} catch (error) {
			console.error('Erreur lors du chargement des options de filtre :', error);
			toast.error('Erreur lors du chargement des options de filtre', {
				position: 'top-center',
				autoClose: 3000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});
		}
	};

	useEffect(() => {
		fetchDashboardData();
		// fetchFilterOptions();
	}, []);

	useEffect(() => {
		const fetchOrders = async () => {
			try {
				// Utiliser une nouvelle route spécifique pour le dashboard
				console.log("Paramètres d'appel API pour le dashboard:", filters);
				
				const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/back-office/dashboard/orders`, {
					params: {
						page: currentPage,
						limit: ordersPerPage,
						...filters
					}
				});

				console.log("Réponse API:", response.data);

				if (response.data && response.data.orders) {
					console.log("Commandes reçues:", response.data.orders.length);
					setOrders(response.data.orders);
					setTotal(response.data.total || response.data.orders.length);
				} else {
					console.log("Aucune commande reçue");
					setOrders([]);
					setTotal(0);
				}
			} catch (error) {
				console.error('Erreur lors du chargement des commandes :', error);
				setOrders([]);
				setTotal(0);
				toast.error('Erreur lors du chargement des commandes', {
					position: 'top-center',
					autoClose: 3000,
				});
			}
		};

		fetchOrders();
	}, [currentPage, filters]);

	const onPageChange = (pageNumber) => setCurrentPage(pageNumber);

	const filterOptions = [
		{
			label: 'Date de commande',
			field: 'date',
			type: 'date-range',
		},
		{
			label: 'Restaurant',
			field: 'restaurantId',
			type: 'select',
			options: restaurantOptions,
		},
		{
			label: 'Statut',
			field: 'status',
			type: 'select',
			options: statusOptions,
		},
	];

	const handleApplyFilters = (appliedFilters) => {
		if (appliedFilters.date_start) {
			appliedFilters.date_start = new Date(appliedFilters.date_start).toISOString().split('T')[0];
		}
		if (appliedFilters.date_end) {
			appliedFilters.date_end = new Date(appliedFilters.date_end).toISOString().split('T')[0];
		}

		// Ne pas ajouter de restaurantId par défaut
		const activeFilters = Object.fromEntries(
			Object.entries(appliedFilters).filter(([key, value]) => value !== '' && value !== null)
		);
		setFilters(activeFilters);
		setCurrentPage(1);
	};

	const handlePrintAllLabels = async () => {
		try {
			const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/back-office/orders/export-shabbat`, {
				responseType: 'blob'
			});
			
			// Créer un lien pour télécharger le fichier
			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', 'commandes_shabbat.csv');
			document.body.appendChild(link);
			link.click();
			link.remove();
			
			toast.success('Le fichier CSV des commandes Shabbat a été généré.', {
				position: 'top-center',
				autoClose: 3000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});
		} catch (error) {
			console.error('Erreur lors de l\'export des commandes Shabbat :', error);
			toast.error('Erreur lors de l\'export des commandes Shabbat', {
				position: 'top-center',
				autoClose: 3000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});
		}
	};

	useWebSocket(events.ORDER_CREATED, (data) => {
		console.log('Nouvelle commande arrivée', data.order);

		// Put the new order at the top of the list
		setOrders([data.order, ...orders]);
		
		// Mettre à jour les compteurs
		fetchDashboardData();
	});

	return (
		<BOLayout>
			<h1 className="text-3xl font-bold text-center mb-8">Tableau de bord Rivka Traiteur</h1>

			<div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
				<div className="bg-primary shadow rounded-lg p-4 flex flex-col items-center justify-center">
					<h2 className="text-xl text-white font-bold">Commandes du jour</h2>
					<p className="text-2xl text-white">{dailyOrders}</p>
				</div>

				<div className="bg-primary shadow rounded-lg p-4 flex flex-col items-center justify-center">
					<h2 className="text-xl text-white font-bold">Commandes pour Shabbat</h2>
					<p className="text-2xl text-white">{shabbatOrders}</p>
				</div>

				<div className="bg-primary shadow rounded-lg p-4 flex flex-col items-center justify-center">
					<h2 className="text-xl text-white font-bold">Étiquettes à imprimer</h2>
					<p className="text-white md:text-xl lg:text-2xl">{pendingLabels}</p>
					<button
						className="bg-secondary text-white py-2 px-4 rounded"
						onClick={handlePrintAllLabels}
					>
						Exporter les commandes Shabbat
					</button>
				</div>
			</div>

			<hr className="my-4 border-gray-300 lg:my-8" />

			<div className="flex justify-between items-center mb-4">
				<div className="text-md font-bold flex items-center gap-2">
					<span className="text-white w-8 h-8 rounded-full flex justify-center items-center bg-primary">{total}</span>{' '}
					Total des commandes
				</div>

				<div className="flex gap-4">
					<button
						onClick={() => {
							try {
								console.log("Tentative d'affichage de toutes les commandes");
								axios.get(`${import.meta.env.VITE_API_URL}/api/back-office/dashboard/orders`)
								.then(response => {
									if (response.data && response.data.orders) {
										console.log("Toutes les commandes reçues:", response.data.orders.length);
										setOrders(response.data.orders);
										setTotal(response.data.total || response.data.orders.length);
									}
								})
								.catch(error => {
									console.error("Erreur:", error);
									toast.error("Impossible d'afficher toutes les commandes");
								});
							} catch (error) {
								console.error("Erreur:", error);
							}
						}}
						className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
					>
						Toutes les commandes
					</button>
					
					<FilterComponent
						filterOptions={filterOptions}
						onApplyFilters={handleApplyFilters}
					/>
				</div>
			</div>

			<table className="table table-striped table-bordered table-auto w-full text-center">
				<thead className="border-b border-b-black pb-2 mb-4">
					<tr>
						<th>Statut</th>
						<th>Référence</th>
						<th>Nom du client</th>
						<th>Adresse de livraison</th>
						<th>Actions</th>
					</tr>
				</thead>
				<tbody>
					{orders.length > 0 ? (
						orders.map((order) => {
							// Vérifications de sécurité pour éviter les erreurs
							if (!order) {
								console.warn('Commande invalide (null ou undefined)');
								return null;
							}
							
							// Si la commande n'a pas de propriété delivery ou customer, créer une ligne simplifiée
							if (!order.delivery || !order.delivery.customer || !order.delivery.address) {
								console.warn('Commande avec structure incomplète:', order);
								return (
									<tr key={order.reference || 'unknown'}>
										<td className={`flex justify-center items-center w-8 h-8 rounded-full ${statuses[order.status]?.bgColor || 'bg-gray-200'}`}>
										</td>
										<td>{order.reference || 'N/A'}</td>
										<td>Information non disponible</td>
										<td>Information non disponible</td>
										<td className="flex items-center justify-center gap-2">
											{order.reference && order.restaurant_id && (
												<button
													type="button"
													onClick={() => navigate(`/back-office/restaurants/${order.restaurant_id}/commandes/${order.reference}/details`)}
												>
													<MagnifyingGlass color="#A2924A" width={40} height={40} />
												</button>
											)}
										</td>
									</tr>
								);
							}
							
							return (
								<tr key={order.reference}>
									<td className={`flex justify-center items-center w-8 h-8 rounded-full ${statuses[order.status]?.bgColor}`}>
										{statuses[order.status]?.icon}
									</td>
									<td>{order.reference}</td>
									<td>{order.delivery.customer.firstname} {order.delivery.customer.lastname}</td>
									<td>{order.delivery.address.address}</td>

									<td className="flex items-center justify-center gap-2">
										<button
											type="button"
											onClick={() => navigate(`/back-office/restaurants/${order.restaurant_id}/commandes/${order.reference}/details`)}
										>
											<MagnifyingGlass color="#A2924A" width={40} height={40} />
										</button>
									</td>
								</tr>
							);
						})
					) : (
						<tr>
							<td colSpan="6" className="text-center py-4">
								<div className="flex flex-col items-center p-4">
									<p className="font-medium text-lg mb-2">Aucune commande trouvée</p>
									<p className="text-gray-500">Les commandes apparaîtront ici une fois qu'elles seront créées.</p>
								</div>
							</td>
						</tr>
					)}
				</tbody>
			</table>

			<Pagination
				currentPage={currentPage}
				totalItems={total}
				itemsPerPage={ordersPerPage}
				onPageChange={onPageChange}
			/>

		</BOLayout>
	);
};

export default BODashboard;
